import { WorkspacePanelProjectsAndFolders } from "features/data-management/workspace/workspace/side-panel/projects-and-folders/WorkspacePanelProjectsAndFolders"
import {
    GlobalNavNestedSidebar,
    GlobalNavNestedSidebarItem,
} from "panels/global-nav/GlobalNavNestedSidebar"
import { useParams, Outlet, useLocation } from "react-router"
import { getGenericWorkspacePage } from "./WorkspaceRoutes"
import { WorkspaceBodyProjectsAndFolders } from "features/data-management/workspace/workspace/body/projects-and-folders/WorkspaceBodyProjectsAndFolders"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveWorkspaceId,
    selectFilteredTagIds,
    setFilteredTagIds,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useEffect, useMemo, useRef } from "react"
import { ButtonPrimary, ButtonSecondary, ButtonTertiary } from "@nova/component-btn"
import { openWorkspaceShareModal } from "features/data-management/workspace/context/thunks"
import { WorkspaceSearchBar } from "features/data-management/workspace/workspace/body/navigation-bar/search-bar/WorkspaceSearchBar"
import { useRouter } from "pages"
import {
    useActiveFolder,
    useCreateFolder,
    useCreateProject,
} from "features/data-management/workspace/context/navigation"
import { WorkspaceTreeMaxDepth } from "features/data-management/workspace/constants"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ActivityPage } from "features/activity/ActivityPage"

export function WorkspaceExplorerWrapper() {
    const dispatch = useAppDispatch()
    const { "*": splat } = useParams()
    const { isEditor } = useWorkspacePermissions()

    useEffect(() => {
        let tagIds = splat?.split("/").filter((s) => !!s) as string[] | undefined
        if (!tagIds?.length) tagIds = undefined
        dispatch(setFilteredTagIds(tagIds))
    }, [dispatch, splat])

    const Page = useMemo(
        () =>
            getGenericWorkspacePage({
                Header: () => (
                    <div className="h-full">
                        <WorkspaceExplorerHeader />
                    </div>
                ),
                Content: () => (
                    <div className="pr-1 h-full">
                        <Outlet />
                    </div>
                ),
                maxWidth: "unbound",
            }),
        []
    )
    return (
        <div className="flex h-full">
            <GlobalNavNestedSidebar title="Workspace">
                <div className="flex flex-col gap-2 p-3">
                    <GlobalNavNestedSidebarItem
                        to={{ path: "/explore" }}
                        title="Recent"
                        Icon={Icons.Recent}
                    />
                    {isEditor && (
                        <GlobalNavNestedSidebarItem
                            to={{ path: "/explore/activity" }}
                            title="Activity"
                            Icon={Icons.PlannerReview}
                        />
                    )}
                    <WorkspacePanelProjectsAndFolders />
                </div>
            </GlobalNavNestedSidebar>
            <div className="grow flex justify-center">
                <Page />
            </div>
        </div>
    )
}

export const useWorkspaceExplorerMode = () => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const location = useLocation()
    const router = useRouter("/")
    const recentsUrl = router.preview({
        path: "/workspace/:workspaceId/explore",
        params: { workspaceId: workspaceId },
    })
    const favoritesUrl = router.preview({
        path: "/workspace/:workspaceId/explore/favorites",
        params: { workspaceId },
    })
    const workspaceUrl = router.preview({
        path: "/workspace/:workspaceId/explore/ws",
        params: { workspaceId },
    })
    const activityUrl = router.preview({
        path: "/workspace/:workspaceId/explore/activity",
        params: { workspaceId },
    })
    const mode = useMemo(() => {
        if (location.pathname === recentsUrl) return "recents"
        if (location.pathname === favoritesUrl) return "favorites"
        if (location.pathname === activityUrl) return "activity"
        if (location.pathname.startsWith(workspaceUrl)) return "workspace"
        return "none"
    }, [activityUrl, favoritesUrl, location.pathname, recentsUrl, workspaceUrl])
    return mode
}

const WorkspaceExplorerHeader = () => {
    const dispatch = useAppDispatch()
    const activeFolder = useActiveFolder()
    const filteredTagIds = useAppSelector(selectFilteredTagIds())
    const createFolder = useCreateFolder()
    const createProject = useCreateProject()
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const mode = useWorkspaceExplorerMode()
    const title = useMemo(() => {
        switch (mode) {
            case "recents":
                return "Recents"
            case "favorites":
                return "Favorites"
            case "activity":
                return "Activity"
            case "none":
            case "workspace":
                return (filteredTagIds.length ? activeFolder?.name : "Workspace") || "Workspace"
        }
    }, [activeFolder?.name, filteredTagIds.length, mode])
    const { isEditor } = useWorkspacePermissions(activeFolder?.id)
    const canCreateFolder = isEditor && filteredTagIds.length <= WorkspaceTreeMaxDepth
    if (mode === "activity") {
        return (
            <div className="w-full h-full flex items-center gap-3 max-w-[848px] m-auto">
                <div className="text-[1.375rem] telegraf">{title}</div>
                <small className="text-gray-400">All projects</small>
            </div>
        )
    }
    return (
        <div className="w-full h-full flex justify-between items-center gap-6">
            <div className="text-[1.375rem] telegraf">{title}</div>
            <WorkspaceSearchBar />
            <div className="flex gap-2 items-center shrink-0">
                <ButtonTertiary
                    title="Share"
                    onClick={() =>
                        dispatch(
                            openWorkspaceShareModal(
                                filteredTagIds.length && activeFolder
                                    ? activeFolder.id
                                    : workspaceId
                            )
                        )
                    }
                />
                {canCreateFolder && (
                    <ButtonSecondary
                        LeftIcon={Icons.Plus}
                        RightIcon={Icons.Folder}
                        onClick={() => createFolder({ insideOfId: filteredTagIds.at(-1) })}
                    />
                )}
                {isEditor && (
                    <ButtonPrimary
                        title="New"
                        LeftIcon={Icons.Plus}
                        onClick={() => createProject()}
                    />
                )}
            </div>
        </div>
    )
}

export const WorkspaceExplorerRecentsPage = WorkspaceBodyProjectsAndFolders
export const WorkspaceExplorerFavoritesPage = WorkspaceBodyProjectsAndFolders
export const WorkspaceExplorerWorkspacePage = WorkspaceBodyProjectsAndFolders

export function WorkspaceExplorerActivityPage() {
    const router = useRouter("/workspace/:workspaceId")
    const routerRef = useRef(router)
    routerRef.current = router
    const { isEditor, isLoading } = useWorkspacePermissions()
    useEffect(() => {
        if (isLoading) return
        if (!isEditor) routerRef.current.navigate({ path: "/explore" }, { replace: true })
    }, [isEditor, isLoading])
    if (!isEditor) return null
    return <ActivityPage />
}
